'use client';

import { useState, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { CategoryKey } from './why-reanalyze-data';
import {
  RICH_TEXT_TAGS,
  buildCategories,
  sortCategories,
  buildCategoryMap,
} from './why-reanalyze-data';
import CategoryPieChart from './category-pie-chart';
import CategoryList from './category-list';
import { customRichTextTags } from '@/utils';
import type { ReanalysisCategoryData } from '@/services';

interface WhyReanalyzeProps {
  categories: ReanalysisCategoryData[];
}

export default function WhyReanalyze({ categories }: WhyReanalyzeProps) {
  const t = useTranslations('Reanalysis.whyReanalyze');
  const [activeKey, setActiveKey] = useState<CategoryKey | null>(null);

  const categoryConfigs = useMemo(() => {
    const merged = buildCategories(categories);
    return sortCategories(merged);
  }, [categories]);

  const total = useMemo(
    () => categoryConfigs.reduce((acc, curr) => acc + curr.value, 0),
    [categoryConfigs],
  );

  const categoryMap = useMemo(
    () => buildCategoryMap(categoryConfigs),
    [categoryConfigs],
  );

  const activeCategory = activeKey ? categoryMap.get(activeKey) : undefined;

  const handleHover = useCallback((key: CategoryKey) => {
    setActiveKey(key);
  }, []);

  const handleLeave = useCallback(() => {
    setActiveKey(null);
  }, []);

  const handleToggle = useCallback((key: CategoryKey) => {
    setActiveKey(prev => (prev === key ? null : key));
  }, []);

  return (
    <section className="bg-white py-[60px] md:py-[100px]">
      <div className="mx-auto max-w-[1440px] px-5 md:px-8">
        <div className="flex flex-col gap-[20px] md:gap-[67px]">
          {/* Header */}
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-[6px] text-[20px] font-bold leading-[22px] tracking-[-0.55px] text-[#1a66ff] md:mb-[25px] md:text-[52px] md:leading-[50.5px] md:tracking-[-1.26px]">
              {t('title')}
            </h2>
            <p className="whitespace-pre-line text-[14px] leading-[22px] text-[#45556c] md:text-[24px] md:leading-[34px]">
              {t.rich('description', customRichTextTags)}
            </p>
          </div>

          <div className="flex flex-col items-center gap-[20px] lg:flex-row lg:items-center lg:justify-between lg:gap-0">
            <CategoryPieChart
              categories={categoryConfigs}
              total={total}
              activeKey={activeKey}
              activeCategory={activeCategory}
              onHover={handleHover}
              onLeave={handleLeave}
            />
            <CategoryList
              categories={categoryConfigs}
              activeKey={activeKey}
              onHover={handleHover}
              onLeave={handleLeave}
              onToggle={handleToggle}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
